// src/writer/components/SectionOrderManager.tsx
import React, { useState, useEffect } from 'react';
import { ProjectState } from '../types/project';
import { FrontMatter, BackMatter } from '../types';
import ReorderableSection, { ReorderableItem } from './ReorderableSection';

interface SectionOrderManagerProps {
    projectState: ProjectState;
    onFrontMatterChange: <K extends keyof FrontMatter>(field: K, value: FrontMatter[K]) => void;
    onBackMatterChange: <K extends keyof BackMatter>(field: K, value: BackMatter[K]) => void;
}

const SectionOrderManager: React.FC<SectionOrderManagerProps> = ({
    projectState,
    onFrontMatterChange,
    onBackMatterChange
}) => {
    // Define default section configurations
    const defaultFrontMatterSections: ReorderableItem[] = [
        { id: 'titlePageContent', label: 'Title Page', content: projectState.frontMatter?.titlePageContent || '', isVisible: true, isRequired: true, description: 'Essential first page with title and author' },
        { id: 'copyrightPageContent', label: 'Copyright Page', content: projectState.frontMatter?.copyrightPageContent || '', isVisible: true, isRequired: true, description: 'Legal copyright information and ISBN' },
        { id: 'dedication', label: 'Dedication', content: projectState.frontMatter?.dedication || '', isVisible: true, description: 'Personal dedication to someone special' },
        { id: 'epigraph', label: 'Epigraph', content: projectState.frontMatter?.epigraph || '', isVisible: false, description: 'Inspirational quote or poem' },
        { id: 'tableOfContents', label: 'Table of Contents', content: projectState.frontMatter?.tableOfContents || '', isVisible: true, description: 'Chapter listing with page numbers' },
        { id: 'foreword', label: 'Foreword', content: projectState.frontMatter?.foreword || '', isVisible: false, description: 'Introduction by another person' },
        { id: 'preface', label: 'Preface', content: projectState.frontMatter?.preface || '', isVisible: false, description: 'Author\'s introduction to the work' },
        { id: 'acknowledgmentsFront', label: 'Acknowledgments', content: projectState.frontMatter?.acknowledgmentsFront || '', isVisible: false, description: 'Thank you notes (can be front or back)' },
        { id: 'prologue', label: 'Prologue (Fiction)', content: projectState.frontMatter?.prologue || '', isVisible: projectState.bookMetadata?.fictionVsNonFiction === 'Fiction', description: 'Story opening scene or background' },
        { id: 'introduction', label: 'Introduction (Non-Fiction)', content: projectState.frontMatter?.introduction || '', isVisible: projectState.bookMetadata?.fictionVsNonFiction === 'Non-fiction', description: 'Overview of the book\'s content and purpose' }
    ];

    const defaultBackMatterSections: ReorderableItem[] = [
        { id: 'epilogue', label: 'Epilogue', content: projectState.backMatter?.epilogue || '', isVisible: false, description: 'Concluding section showing future outcomes' },
        { id: 'afterword', label: 'Afterword', content: projectState.backMatter?.afterword || '', isVisible: false, description: 'Author\'s final thoughts and reflections' },
        { id: 'authorsNote', label: 'Author\'s Note', content: projectState.backMatter?.authorsNote || '', isVisible: false, description: 'Personal notes about writing process' },
        { id: 'acknowledgmentsBack', label: 'Acknowledgments', content: projectState.backMatter?.acknowledgmentsBack || '', isVisible: true, description: 'Thank you notes (commonly placed here)' },
        { id: 'aboutTheAuthor', label: 'About the Author', content: projectState.backMatter?.aboutTheAuthor || '', isVisible: true, description: 'Author biography and credentials' },
        { id: 'readingGroupGuide', label: 'Reading Group Guide', content: projectState.backMatter?.readingGroupGuide || '', isVisible: false, description: 'Discussion questions for book clubs' },
        { id: 'teaserNextBook', label: 'Next Book Teaser', content: projectState.backMatter?.teaserNextBook || '', isVisible: false, description: 'Preview of upcoming work' },
        { id: 'bibliography', label: 'Bibliography', content: projectState.backMatter?.bibliography?.length ? 'Bibliography entries configured' : '', isVisible: projectState.bookMetadata?.fictionVsNonFiction === 'Non-fiction', description: 'Sources and references (for non-fiction)' },
        { id: 'glossary', label: 'Glossary', content: projectState.backMatter?.glossary?.length ? 'Glossary terms configured' : '', isVisible: false, description: 'Definitions of technical terms' },
        { id: 'index', label: 'Index', content: projectState.backMatter?.index || '', isVisible: projectState.bookMetadata?.fictionVsNonFiction === 'Non-fiction', description: 'Alphabetical listing of topics and page numbers' }
    ];

    const [frontMatterSections, setFrontMatterSections] = useState<ReorderableItem[]>(defaultFrontMatterSections);
    const [backMatterSections, setBackMatterSections] = useState<ReorderableItem[]>(defaultBackMatterSections);

    // Update sections when project state changes
    useEffect(() => {
        setFrontMatterSections(defaultFrontMatterSections);
        setBackMatterSections(defaultBackMatterSections);
    }, [projectState]);

    const handleFrontMatterReorder = (newOrder: ReorderableItem[]) => {
        setFrontMatterSections(newOrder);
        // Note: In a full implementation, you might want to save the custom order
        // This could be saved as metadata in the project state
    };

    const handleBackMatterReorder = (newOrder: ReorderableItem[]) => {
        setBackMatterSections(newOrder);
        // Note: In a full implementation, you might want to save the custom order
    };

    const handleFrontMatterToggleVisibility = (itemId: string) => {
        setFrontMatterSections(prev => 
            prev.map(item => 
                item.id === itemId ? { ...item, isVisible: !item.isVisible } : item
            )
        );
    };

    const handleBackMatterToggleVisibility = (itemId: string) => {
        setBackMatterSections(prev => 
            prev.map(item => 
                item.id === itemId ? { ...item, isVisible: !item.isVisible } : item
            )
        );
    };

    const moveFrontToBack = (itemId: string) => {
        const item = frontMatterSections.find(section => section.id === itemId);
        if (!item || item.isRequired) return;

        // For this demo, we'll show an alert. In a full implementation,
        // you would implement the actual movement logic
        alert(`Moving "${item.label}" to Back Matter. This would be implemented in the full version.`);
    };

    const moveBackToFront = (itemId: string) => {
        const item = backMatterSections.find(section => section.id === itemId);
        if (!item || item.isRequired) return;

        alert(`Moving "${item.label}" to Front Matter. This would be implemented in the full version.`);
    };

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Section Order Management</h2>
                <p className="text-gray-600 mb-6">
                    Customize the order and visibility of your book's front matter and back matter sections. 
                    Drag and drop to reorder, hide sections you don't need, and move elements between front and back matter.
                </p>
                
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h3 className="font-semibold text-blue-800 mb-2">📖 Professional Structure Guidelines</h3>
                    <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-700">
                        <div>
                            <h4 className="font-medium mb-1">Front Matter Order:</h4>
                            <p className="text-xs">Title Page → Copyright → Dedication → Table of Contents → Foreword/Preface → Prologue/Introduction</p>
                        </div>
                        <div>
                            <h4 className="font-medium mb-1">Back Matter Order:</h4>
                            <p className="text-xs">Epilogue → Acknowledgments → About Author → Appendices → Bibliography → Index</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
                <ReorderableSection
                    title="📖 Front Matter Sections"
                    items={frontMatterSections}
                    onReorder={handleFrontMatterReorder}
                    onToggleVisibility={handleFrontMatterToggleVisibility}
                    onMoveToOtherSection={moveFrontToBack}
                    otherSectionName="Back Matter"
                />

                <ReorderableSection
                    title="📚 Back Matter Sections"
                    items={backMatterSections}
                    onReorder={handleBackMatterReorder}
                    onToggleVisibility={handleBackMatterToggleVisibility}
                    onMoveToOtherSection={moveBackToFront}
                    otherSectionName="Front Matter"
                />
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Section Status Summary</h3>
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-medium text-gray-700 mb-2">Front Matter</h4>
                        <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                                <span>Total sections:</span>
                                <span className="font-medium">{frontMatterSections.length}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Visible sections:</span>
                                <span className="font-medium text-blue-600">{frontMatterSections.filter(s => s.isVisible).length}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Completed sections:</span>
                                <span className="font-medium text-green-600">{frontMatterSections.filter(s => s.content?.trim()).length}</span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <h4 className="font-medium text-gray-700 mb-2">Back Matter</h4>
                        <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                                <span>Total sections:</span>
                                <span className="font-medium">{backMatterSections.length}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Visible sections:</span>
                                <span className="font-medium text-blue-600">{backMatterSections.filter(s => s.isVisible).length}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Completed sections:</span>
                                <span className="font-medium text-green-600">{backMatterSections.filter(s => s.content?.trim()).length}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SectionOrderManager;